"use client";

import { ArrowRight, Sparkles } from "lucide-react";
import { ReactNode } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

interface ServiceCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  href: string;
  color?: string; // Tailwind color class like 'text-red-500'
  bgColor?: string; // Tailwind background class like 'bg-red-50'
  hoverColor?: string; // Tailwind color class for hover like 'group-hover:text-red-500'
  hoverBorder?: string; // Tailwind border class like 'group-hover:border-red-500'
  compact?: boolean;
}

const ServiceCard = ({ 
  title, 
  description, 
  icon, 
  href, 
  color = "text-primary", 
  bgColor = "bg-primary/5",
  hoverColor = "group-hover:text-primary",
  hoverBorder = "group-hover:border-primary",
  compact = false
}: ServiceCardProps) => (
  <motion.article 
    className={`h-full ${compact ? 'px-0 py-2' : 'px-2 py-4'}`}
    whileHover={{ y: compact ? 0 : -5, x: compact ? 5 : 0 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
  >
    <Link 
      href={href}
      className={`group relative flex ${compact ? 'flex-row items-center gap-4 p-4' : 'flex-col p-6 md:p-8'} h-full rounded-[24px] md:rounded-[32px] bg-white border border-border hover:border-transparent transition-all duration-500 overflow-hidden shadow-xs hover:shadow-xl`}
    >
      {/* Dynamic Background Pattern */}
      <div className={`absolute top-0 right-0 -mr-16 -mt-16 ${compact ? 'w-32 h-32' : 'w-48 h-48'} ${bgColor} rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-500`} />
      
      {/* Icon Wrapper */}
      <div className={`relative ${compact ? 'w-10 h-10' : 'w-14 h-14 md:w-16 md:h-16'} rounded-xl md:rounded-2xl ${bgColor} shrink-0 flex items-center justify-center ${color} ${compact ? '' : 'mb-6'} group-hover:bg-white transition-all duration-500 shadow-inner overflow-hidden`}>
        <div className={`${compact ? 'w-5 h-5' : 'w-7 h-7 md:w-8 md:h-8'} transition-transform duration-500 group-hover:scale-110 z-10`}>
            {icon}
        </div>
        
        {/* Floating Sparkle Icon */}
        {!compact && (
          <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 z-20">
              <Sparkles className="w-4 h-4" />
          </div>
        )}
        
        {/* Color fill on hover */}
        <div className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-current opacity-10`} />
      </div>

      {/* Content */}
      <div className="relative space-y-1 flex-1">
        <h3 className={`${compact ? 'text-base' : 'text-xl md:text-2xl'} font-display font-bold text-foreground leading-tight ${hoverColor} transition-colors duration-300`}>
            {title}
        </h3>
        {!compact && (
          <p className="text-muted-foreground text-sm md:text-base leading-relaxed line-clamp-3">
              {description}
          </p>
        )}
      </div>

      {/* Footer / CTA arrow only for compact */}
      {compact ? (
        <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center ${color} group-hover:scale-110 transition-all duration-300`}>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
        </div>
      ) : (
        <div className="relative mt-6 pt-4 border-t border-border flex items-center justify-between group-hover:border-border/0 transition-colors duration-300">
          <span className={`text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ${hoverColor} transition-colors`}>
              RÉSZLETEK
          </span>
          <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center ${color} group-hover:scale-110 transition-all duration-300`}>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </div>
        </div>
      )}
      
      {/* Border color highlight on hover */}
      <div className={`absolute inset-0 rounded-[24px] md:rounded-[32px] border-2 border-transparent ${hoverBorder} opacity-20 pointer-events-none transition-all duration-500`} />
    </Link>
  </motion.article>
);


export default ServiceCard;
